import { createStore } from "vuex";
import createPersistedState from "vuex-persistedstate";
import type { TariffOptionStruct, TariffArray } from "@/struct/TariffStruct";
import { SubscriptionResponse } from "@/struct/SubscriptionDataStruct";
import { getLocalStorageParams, setLocalStorageParams } from "@/utils/common";
import brandModule from "@/store/modules/brand";
import productModule from "@/store/modules/product";
import systemModule from "@/store/modules/system";
import authModule from "@/store/modules/auth";
import customerModule from "@/store/modules/customer";
import tariffModule from "@/store/modules/tariff";
import premiumModule from "@/store/modules/premium";
import { allBrands, defaultBrand } from "@/config/brands";

const getDefaultState = () => ({
  isLogoHidden: false,
  currentPage: "",
  tariffArray: [] as TariffArray[],
  isAsideHidden: false,
  subscriptionDetails: {} as SubscriptionResponse,
  showPaymentModal: false,
  visitCount: getLocalStorageParams("visitCount") || 0,
  feedbackShownVisits: getLocalStorageParams("feedbackShownVisits") || [],
  feedbackSubmitted: getLocalStorageParams("feedbackSubmitted") || false,
});
export default createStore({
  state: getDefaultState(),
  getters: {
    isLogoHidden: (state) => state.isLogoHidden,
    currentPage: (state) => state.currentPage,
    tariffArray: (state) => state.tariffArray,
    subscriptionDetails: (state) => state.subscriptionDetails,
    isAsideHidden: (state) => state.isAsideHidden,
    showPaymentModal: (state) => state.showPaymentModal,
    visitCount: (state) => state.visitCount,
    feedbackShownVisits: (state) => state.feedbackShownVisits,
    feedbackSubmitted: (state) => state.feedbackSubmitted,
  },
  mutations: {
    SET_LOGO_VISIBILITY(state, isHidden: boolean) {
      state.isLogoHidden = isHidden;
    },
    SET_CURRENT_PAGE(state, page: string) {
      state.currentPage = page;
    },
    SET_TARIFF_ARRAY(state, value: TariffArray[]) {
      state.tariffArray = value;
    },
    SET_ASIDE(state, isHidden: boolean) {
      state.isAsideHidden = isHidden;
    },
    SET_SUBSCRIPTION_DETAILS(state, value: SubscriptionResponse) {
      state.subscriptionDetails = value;
    },
    SET_SHOW_PAYMENT_MODAL(state, isShowed: boolean) {
      state.showPaymentModal = isShowed;
    },
    SET_VISIT_COUNT(state, value: number) {
      state.visitCount = value;
    },
    SET_FEEDBACK_SHOWN_VISITS(state, value: number[]) {
      state.feedbackShownVisits = value;
    },
    SET_FEEDBACK_SUBMITTED(state, value: boolean) {
      state.feedbackSubmitted = value;
    },
  },
  actions: {
    //new
    clearLocalDataStore({ dispatch, state }) {
      //Удаление конф. данных из локального стора paths: ["subscriptionDetails", "mainTariff", "brand.operatorSource"],
      //в том числе и всех данные по тарифу
      dispatch("tariff/clearTariffStore", null, { root: true });
      dispatch("brand/removeOperatorSource", defaultBrand, { root: true });
    },
    //old

    updateLogoVisibility({ commit }, isHidden: boolean) {
      commit("SET_LOGO_VISIBILITY", isHidden);
    },
    setTariffArray({ commit }, value: TariffArray[]) {
      commit("SET_TARIFF_ARRAY", value);
    },
    setSubscriptionDetails({ commit }, value: SubscriptionResponse) {
      commit("SET_SUBSCRIPTION_DETAILS", value);
    },
    updateCurrentPage({ commit }, page: string) {
      commit("SET_CURRENT_PAGE", page);
    },
    updateAsideVisibility({ commit }, isHidden: boolean) {
      commit("SET_ASIDE", isHidden);
    },
    togglePaymentModal({ commit }, isShowed: boolean) {
      commit("SET_SHOW_PAYMENT_MODAL", isShowed);
    },
    updateVisitCount({ commit }, value: number) {
      setLocalStorageParams("visitCount", value);
      commit("SET_VISIT_COUNT", value);
    },
    incrementVisitCount({ state, dispatch }) {
      const nextCount = Number(state.visitCount || 0) + 1;
      dispatch("updateVisitCount", nextCount);
    },
    updateFeedbackShownVisits({ commit }, value: number[]) {
      setLocalStorageParams("feedbackShownVisits", value);
      commit("SET_FEEDBACK_SHOWN_VISITS", value);
    },
    updateFeedbackSubmitted({ commit }, value: boolean) {
      setLocalStorageParams("feedbackSubmitted", value);
      commit("SET_FEEDBACK_SUBMITTED", value);
    },
    addFeedbackShownVisit({ state, dispatch }, visit: number) {
      const current = Array.isArray(state.feedbackShownVisits)
        ? state.feedbackShownVisits
        : [];
      if (!current.includes(visit)) {
        dispatch("updateFeedbackShownVisits", [...current, visit]);
      }
    },
  },
  modules: {
    brand: brandModule,
    product: productModule,
    system: systemModule,
    auth: authModule,
    customer: customerModule,
    tariff: tariffModule,
    premium: premiumModule,
  },
  plugins: [
    createPersistedState({
      key: "moi-operator-store", // optional, default is "vuex"
      storage: window.localStorage, // or window.sessionStorage
      paths: [
        "tariff.subscriptionDetails",
        "tariff.mainTariff",
        "brand.operatorSource",
      ], // optional, persist only specific parts of state
      getState: (key) => {
        const rawValue = localStorage.getItem(key);
        if (!rawValue) return undefined;

        try {
          const parsed = JSON.parse(rawValue);

          if (
            parsed.brand &&
            !allBrands.includes(parsed.brand.operatorSource)
          ) {
            console.warn(
              "Обнаружен невалидный бренд в localStorage! Сброс на дефолтный."
            );
            parsed.brand.operatorSource = defaultBrand;
          }
          return parsed;
        } catch (e) {
          localStorage.removeItem(key);
          return undefined;
        }
      },
    }),
  ],
});
